import { useState } from 'react';
import { DownloadGate } from '@suvoda-uat/ui-suvoda';
import type { PrepareDownloadResult, RenderReport } from '../lib/api-client.js';
import { usePrepareDownload, useRenderReport, useTrial } from '../lib/queries.js';

/** The `notAvailable` shape returned by PrepareDownload (no draft / not finalised / regressed). */
export type DownloadNotAvailable = Extract<
  PrepareDownloadResult['result'],
  { notAvailable: unknown }
>['notAvailable'];

export type DownloadViewProps = {
  trialId: string;
  /**
   * The latest PrepareDownload `notAvailable` result (the gate / unavailable
   * message). Owned by {@link useDownloadTestScript}, which the trigger button
   * (now in the topbar, FR-034) shares with this body so a click there surfaces
   * the gate here.
   */
  notAvailable?: DownloadNotAvailable | null;
  /** Jump to an unresolved Test Case from the gate (the route navigates). */
  onSelectTestCase?: (testCaseId: string) => void;
};

/**
 * The Download flow's interactive core (US3, FR-034), lifted out of the body so
 * the trigger can live in the topbar while the gate/report stay in the Assembly
 * body. Runs PrepareDownload; an `available` result is handed to the browser
 * download, a `notAvailable` result is returned for the body to render.
 */
export function useDownloadTestScript(
  trialId: string,
  onReady?: (presignedUrl: string) => void,
): {
  onDownload: () => void;
  pending: boolean;
  notAvailable: DownloadNotAvailable | null;
} {
  const prepare = usePrepareDownload(trialId);
  const [notAvailable, setNotAvailable] = useState<DownloadNotAvailable | null>(null);
  const onDownload = () => {
    setNotAvailable(null);
    prepare.mutate(undefined, {
      onSuccess: ({ result }) => {
        if ('available' in result) {
          (onReady ?? triggerBrowserDownload)(result.available.url);
        } else {
          setNotAvailable(result.notAvailable);
        }
      },
    });
  };
  return { onDownload, pending: prepare.isPending, notAvailable };
}

/** Default browser-download trigger: a transient anchor click on the presigned URL. */
const triggerBrowserDownload = (presignedUrl: string): void => {
  const link = document.createElement('a');
  link.href = presignedUrl;
  link.rel = 'noopener';
  // No `download` attribute — the presigned URL's Content-Disposition names the file.
  document.body.appendChild(link);
  link.click();
  link.remove();
};

/**
 * Download flow for the Assembly view (US3, FR-034). The Download button runs
 * PrepareDownload; the server serves the latest finalised artefact only. An
 * `available` result is handed to the browser-download trigger; a
 * `notAvailable.outstanding` result renders the DownloadGate so the designer can
 * jump to unresolved Test Cases. Fuller export/refusal copy lands in T104/T105.
 */

const unavailableMessage = (
  notAvailable: Extract<PrepareDownloadResult['result'], { notAvailable: unknown }>['notAvailable'],
): string | null => {
  if (notAvailable.reason === 'no_draft') return null;
  if (notAvailable.outstanding !== undefined) return null;
  if (notAvailable.changesSinceVersion !== undefined) {
    return `Changes since ${notAvailable.changesSinceVersion}`;
  }
  return 'Finalise to export';
};

/** F-013 (R11/FR-016): the per-version render report, shown when finalised. */
function RenderReportPanel({ report }: { readonly report: RenderReport }) {
  return (
    <section
      aria-label="Render report"
      className="flex flex-col gap-2 rounded border border-border-default p-3 text-sm"
    >
      <h3 className="m-0 text-sm font-semibold">Render report</h3>
      {report.warnings.length === 0 && report.placeholders.length === 0 ? (
        <p className="m-0 text-text-secondary">No warnings or unresolved placeholders.</p>
      ) : null}
      {report.warnings.length > 0 ? (
        <ul className="m-0 list-disc pl-5">
          {report.warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      ) : null}
      {report.placeholders.length > 0 ? (
        <ul className="m-0 list-disc pl-5">
          {report.placeholders.map((placeholder) => (
            <li key={`${placeholder.kind}:${placeholder.text}:${placeholder.context}`}>
              <span className="font-mono">{placeholder.text}</span>{' '}
              <span className="text-text-secondary">
                ({placeholder.kind}, near “{placeholder.context}”, ×{placeholder.count})
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

export function DownloadView({ trialId, notAvailable = null, onSelectTestCase }: DownloadViewProps) {
  const { data: trial } = useTrial(trialId);
  const finalisedVersion = trial?.latestFinalisedVersion?.version;
  const { data: report } = useRenderReport(trialId, finalisedVersion);

  return (
    <div data-download-body className="flex flex-col gap-3">
      {/* The Download trigger lives in the topbar (FR-034); this body holds the
          gate + render report it produces. */}
      {notAvailable?.reason === 'not_finalised' && notAvailable.outstanding && (
        <DownloadGate
          outstanding={notAvailable.outstanding}
          {...(onSelectTestCase && { onSelectTestCase })}
        />
      )}
      {notAvailable ? (
        unavailableMessage(notAvailable) ? (
          <p className="m-0 text-sm font-semibold text-text-secondary">
            {unavailableMessage(notAvailable)}
          </p>
        ) : null
      ) : null}
      {report !== undefined ? <RenderReportPanel report={report} /> : null}
    </div>
  );
}
